import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Table, Tag, Alert } from '../../ui';
import { request } from '../../api/client';
import { fmtQty, variantText, isRedundantSku } from '../../lib/format';
import { PALETTE } from '../../lib/palette';

/**
 * WHAT IS ON THE ORDER — and whether the warehouse being chosen can ship it.
 *
 * ONE definition, used by the order page's approve dialog AND by the approvals hub's decision
 * dialog. The hub used to name the goods in a single grey line ("Contents · AGN × 1") and say
 * nothing at all about stock, so the same approval was a briefed decision from Sales → Order
 * approval and a guess from the Approvals hub. Two renderings of the same question drift; one
 * cannot.
 *
 * The warehouse is OPTIONAL. Before one is picked the panel still answers "what has been ordered
 * and how much", plus what exists across every warehouse; picking one adds the per-warehouse
 * columns and the coverage verdict. (The stock-plan endpoint is happy without a warehouse — it
 * falls back to the order's own dispatch warehouse, or to none.)
 *
 * Nothing here reserves stock. It is advisory, exactly as it is on the order screen.
 */
export function OrderStockLines({ orderId, warehouseId, beforeTable }: {
  orderId: string;
  /** The warehouse under consideration. Empty = nothing picked yet (see above). */
  warehouseId?: string | null;
  /** Rendered between the coverage banners and the table — the order page slots its GST band here. */
  beforeTable?: ReactNode;
}) {
  const wid = warehouseId ? String(warehouseId) : undefined;
  // Same query key the order page's approve dialog uses, so the two share one fetch and can never
  // show two different answers for the same order + warehouse.
  const { data: plan, isFetching: planLoading } = useQuery({
    queryKey: ['order-stock-plan', orderId, wid],
    queryFn: () => request<any>({ url: `/orders/${orderId}/stock-plan`, params: wid ? { warehouseId: wid } : {} }),
    enabled: !!orderId,
  });

  const lines: any[] = plan?.lines ?? [];
  const short = !!wid && !!plan && !plan.fullyAvailableHere;
  const totalShort = lines.some((l) => Number(l.availableTotal ?? 0) < Number(l.pendingQty ?? 0)); // even all-WH can't cover
  // Advisory (no reservation): some physical stock here is already spoken-for by other active orders.
  const committedHere = Number(plan?.committedHereTotal ?? 0) > 0;
  const freeCovers = !!plan?.freeCoversHere;

  return (
    <>
      {wid && plan && (
        !plan.fullyAvailableHere
          ? <Tag tone="gold" style={{ marginBottom: 8 }}>Short on some items here</Tag>
          : freeCovers
            ? <Tag tone="green" style={{ marginBottom: 8 }}>✓ This warehouse fully covers the order</Tag>
            : <Tag tone="blue" style={{ marginBottom: 8 }}>Physically covers — but some stock is committed to other orders</Tag>
      )}
      {wid && short && totalShort && (
        <Alert type="warning" style={{ marginBottom: 8 }}
          title="Stock is short even across all warehouses"
          description="Transfers can't fully cover this order. Approve to fulfil what's available — the remainder is backordered and releases when stock is replenished (e.g. a production run)." />
      )}

      {beforeTable}

      {wid && committedHere && (
        <Alert type="info" style={{ marginBottom: 8 }}
          title="Some stock here is already committed to other orders"
          description="The table shows physical stock here, how much is already spoken-for by other active orders assigned to this warehouse, and what's free for this order. Advisory only — nothing is reserved. Decide as you see fit: bring stock in, backorder, or prioritise another order." />
      )}

      {/*
        * AN ORDER WITH NOTHING ON IT IS A FAULT, not an empty table.
        *
        * "No lines" in a table's grey empty slot is the quietest way to say the loudest thing on this
        * screen: approving would open a priced order containing no goods. Said in amber, in the words
        * the queue row uses for the same gap.
        */}
      {plan && !lines.length ? (
        <Alert type="warning" title="No items on this order" style={{ marginBottom: 8 }}
          description="There is nothing to pack or dispatch. Send it back for correction rather than approving it." />
      ) : (
        <Table size="small" loading={planLoading} pagination={false} rowKey="orderItemId" dataSource={lines}
          locale={{ emptyText: 'Reading the order…' }}
          columns={[
            { title: 'Product', dataIndex: 'productName', render: (v: any, l: any) => {
              const variant = variantText(l.packSize, l.uom, l.variantLabel);
              return (
                <div>
                  <div>{v ?? l.productId}{variant ? <span style={{ color: 'var(--gb-text-muted)' }}> · {variant}</span> : ''}</div>
                  {l.sku && !isRedundantSku(l.sku, `${v ?? ''} ${variant}`, v) && <div style={{ fontSize: 11, color: 'var(--gb-text-muted)' }}>{l.sku}</div>}
                </div>
              );
            } },
            { title: 'Ordered', dataIndex: 'pendingQty', align: 'right' as const, width: 84, render: (v: any) => fmtQty(v) },
            // NO WAREHOUSE PICKED YET: there is no "here" to report, so the columns that would all
            // read zero are simply not drawn — a table of zeroes says "no stock anywhere", which is
            // a different and usually untrue statement.
            ...(!wid ? [] : committedHere
              // When other orders have committed stock here, show physical → committed → free (advisory);
              // otherwise keep the plain physical view.
              ? [
                  { title: 'Physical here', dataIndex: 'availableHere', align: 'right' as const, width: 104, render: (v: any) => fmtQty(v) },
                  { title: 'Committed', dataIndex: 'committedElsewhere', align: 'right' as const, width: 100, render: (v: any) => (Number(v) > 0 ? <span style={{ color: 'var(--color-warning-600)' }}>−{fmtQty(v)}</span> : '—') },
                  { title: 'Free here', dataIndex: 'freeHere', align: 'right' as const, width: 96, render: (v: any, l: any) => <b style={{ color: Number(v) < Number(l.pendingQty) ? PALETTE.warning[600] : PALETTE.success[600] }}>{fmtQty(v)}</b> },
                ]
              : [
                  { title: 'Available here', dataIndex: 'availableHere', align: 'right' as const, width: 116, render: (v: any, l: any) => <span style={{ color: l.shortHere > 0 ? PALETTE.warning[600] : undefined }}>{fmtQty(v)}</span> },
                  { title: 'Short', dataIndex: 'shortHere', align: 'right' as const, width: 84, render: (v: any) => (v > 0 ? <b style={{ color: 'var(--color-warning-600)' }}>{fmtQty(v)}</b> : '—') },
                ]),
            { title: wid ? 'Total (all WH)' : 'In stock (all WH)', dataIndex: 'availableTotal', align: 'right' as const, width: 116, render: (v: any, l: any) => <span style={{ color: Number(v) < Number(l.pendingQty) ? PALETTE.warning[600] : undefined }}>{fmtQty(v)}</span> },
          ]} />
      )}
    </>
  );
}
